import globalCss from '../../global.module.css'

import { Button, Modal } from "react-bootstrap";
import LoadingSpinner from '../Loading/LoadingSpinner';
import React from 'react';
import { useTranslation } from "react-i18next";
import ConditionalRender from "../ConditionalRender";

/**
 * Confirmation modal dialog.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.active - If true, modal is visible.
 * @param {string|JSX.Element} props.title - Modal title.
 * @param {React.ReactNode} props.children - Modal content/body.
 * @param {function} props.onCancel - Called when cancel is clicked or modal is closed.
 * @param {function} props.onConfirm - Called when confirm/positive button is clicked.
 * @param {Object} [props.positiveButton] - Positive button configuration.
 * @param {string} [props.positiveButton.variant='danger'] - Bootstrap variant for the positive button.
 * @param {string|null} [props.positiveButton.text] - Button text. Defaults to translation of 'confirm'.
 * @param {boolean} [props.positiveButton.disabled=false] - If true, disables the button.
 * @param {string|null} [props.negativeButtonText=null] - Text for negative/cancel button. Defaults to translation of 'cancel'.
 * @param {boolean} [props.showSpinner=false] - If true, shows a loading spinner instead of buttons.
 * @param {'sm'|'lg'|null} [props.size=null] - Modal size ('sm', 'lg', or null for default).
 * @param {function(): [boolean, string] | null} [props.validator=null]
 *        - Function to check validity. Should return [isValid, description]`.
 *          If `valid` is false, the confirm button is disabled and the `description` is shown as an error message.
 */
const ConfirmModal = ({ active, title, children, onCancel, onConfirm, positiveButton, negativeButtonText = null, showSpinner, size = null, validator = null, hideNegativeButton = false }) => {
    const { t } = useTranslation()

    if (!hideNegativeButton) {
        if (!negativeButtonText) {
            negativeButtonText = t('global.operations.cancel')
        }
    }
    if (!positiveButton?.text) {
        positiveButton.text = t('global.operations.confirm')
    }

    const [valid, description] = validator ? validator() : [true, ''];
    const disableButton = positiveButton?.disabled ? positiveButton.disabled : !valid;

    return (
        <Modal centered show={active} onHide={() => { !showSpinner && onCancel() }} size={size}>
            <Modal.Header className='modal-header' closeButton={!hideNegativeButton ? true : false}>

                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {children}
            </Modal.Body>
            <Modal.Footer>
                {
                    showSpinner ?
                        <LoadingSpinner variant={positiveButton?.variant ? positiveButton.variant : "danger"} className='mx-4' /> :
                        <>
                            <ConditionalRender condition={!valid}>
                                <p className={'text-danger'} style={{ position: 'absolute', left: 10 }}>{description}</p>
                            </ConditionalRender>
                            {!hideNegativeButton && (
                                <button className={globalCss.neutralBtn} onClick={onCancel}>{negativeButtonText}</button>
                            )}

                            <Button variant={positiveButton?.variant ? positiveButton.variant : "danger"} disabled={disableButton} onClick={() => onConfirm()}>{positiveButton?.text ? positiveButton.text : "Confirm"}</Button>
                        </>
                }
            </Modal.Footer>
        </Modal>
    );
};

ConfirmModal.defaultProps = {
    positiveButton: { variant: 'danger', text: null, disabled: false }
}

export default ConfirmModal;